/* Retrowave: variant / Ajax cart patch for product pages AND dynamically
   injected Shrine quick-add modals. No styling changes. */
(() => {
  'use strict';
  // Do not exit on homepage: quick-add product forms are injected AFTER load.

  const escapeId = value => window.CSS && CSS.escape ? CSS.escape(String(value)) : String(value).replace(/[^\w-]/g, '\\$&');
  const sectionFor = picker => picker && picker.dataset.section;
  const productPicker = section => document.getElementById(`variant-selects-${section}`);
  const formFor = section => document.getElementById(`product-form-${section}`);
  const qtyFor = section => document.getElementById(`quantity-breaks-${section}`);
  const money = amount => {
    const currency = window.Shopify?.currency?.active || window.ShopifyAnalytics?.meta?.currency || 'USD';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style: 'currency', currency}).format(amount / 100); }
    catch (_) { return '$' + (amount / 100).toFixed(2); }
  };
  const activeBreak = section => {
    const block = qtyFor(section);
    if (!block) return null;
    const checked = block.querySelector('input[name="quantity"]:checked');
    if (!checked) return null;
    const label = block.querySelector(`label[for="${escapeId(checked.id)}"]`) ||
      [...block.querySelectorAll('.quantity-break')].find(el => el.dataset.input === checked.dataset.input);
    return label ? {quantity: parseInt(checked.value, 10) || 1, label, root: block} : null;
  };
  const variants = picker => {
    if (!picker) return [];
    try { return JSON.parse(picker.querySelector('script[type="application/json"]')?.textContent || '[]'); }
    catch (_) { return []; }
  };
  function chosenVariant(picker) {
    if (!picker) return null;
    const options = [...picker.querySelectorAll('.product-form__input')].map(group => {
      const dropdown = group.querySelector('select.select__select');
      return dropdown ? dropdown.value : group.querySelector('input[type="radio"]:checked')?.value;
    });
    return variants(picker).find(v => v.options?.every((value, i) => String(options[i]) === String(value))) || null;
  }
  // Single-variant products (such as Resilia) do not render <variant-selects>.
  // Their Shopify variant ID and base price are still available on the product
  // form and the native Quantity Breaks element. Use them ONLY in that case.
  function selectedVariantForSection(section) {
    const picker = productPicker(section);
    if (picker) return chosenVariant(picker);
    const qb = qtyFor(section);
    const form = formFor(section);
    if (!qb || !form) return null;
    const variantId = Number(form.querySelector('input[name="id"]')?.value);
    const basePrice = Number(qb.dataset.basePrice);
    const comparePrice = Number(activeBreak(section)?.label?.dataset.baseComparePrice);
    if (!Number.isSafeInteger(variantId) || variantId <= 0 || !Number.isFinite(basePrice) || basePrice < 0) return null;
    return {
      id: variantId,
      price: basePrice,
      compare_at_price: Number.isFinite(comparePrice) && comparePrice > 0 ? comparePrice : basePrice
    };
  }
  function updatePrice(section, variant) {
    if (!variant) return;
    const qb = activeBreak(section);
    const useTotal = qb?.root.dataset.updatePdpPrices === 'true';
    const multiplier = useTotal ? qb.quantity : 1;
    const percent = useTotal ? parseFloat(qb.label.dataset.percentageLeft || '1') : 1;
    const fixed = useTotal ? parseFloat(qb.label.dataset.fixedDiscount || '0') : 0;
    const price = Math.max(0, Math.round(variant.price * multiplier * percent - fixed));
    const compare = (variant.compare_at_price || variant.price) * multiplier;
    const priceWraps = [document.getElementById(`price-${section}`), document.getElementById(`sticky-atc-separate-price-${section}`)];
    for (const wrap of priceWraps) {
      if (!wrap) continue;
      wrap.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale').forEach(el => {el.textContent = money(price);});
      wrap.querySelectorAll('.price__compare-price .price-item--regular').forEach(el => {el.textContent = money(compare);});
      wrap.querySelectorAll('.price').forEach(el => el.classList.toggle('price--on-sale', compare > price));
    }
    const btnPrice = document.getElementById(`main-atc-price-${section}`);
    if (btnPrice) {
      const span = btnPrice.querySelector(`[class^="main-atc-price-span"]`);
      if (span) span.textContent = money(price);
    }
    const stickyPrice = document.getElementById(`sticky-atc-price-${section}`);
    if (stickyPrice) {
      const span = stickyPrice.querySelector('.price-item');
      if (span) span.textContent = money(price);
    }
  }
  // Shopify's native accelerated checkout reads a single product form rather than
  // Shrine's custom quantity-breaks widget. Keep its id AND quantity in that form.
  // Checkout must have exactly ONE successful quantity control. Shrine's
  // Quantity Breaks radios use form="product-form-...", making them part of the
  // Shopify form even though they are elsewhere in the page. V10 added a second
  // name="quantity" control, which left native accelerated checkout with an
  // ambiguous (often default Buy 2) form value.
  function checkoutQuantityControl(section, form) {
    const qb = qtyFor(section);
    if (qb) {
      // Detach only the OUTSIDE radio controls from the checkout form. Their
      // name/checked state and theme click/change logic remain intact.
      qb.querySelectorAll('input[type="radio"][name="quantity"]').forEach(radio => {
        if (!form.contains(radio) && radio.getAttribute('form') === form.id) {
          radio.removeAttribute('form');
        }
      });
    }
    const inForm = [...form.querySelectorAll('input[name="quantity"]')];
    let canonical = inForm.find(el => el.dataset.retrowaveCheckoutQuantity === 'true') ||
      inForm.find(el => el.type !== 'radio' && el.type !== 'checkbox');
    if (!canonical) {
      canonical = document.createElement('input');
      canonical.type = 'hidden';
      canonical.name = 'quantity';
      canonical.dataset.retrowaveCheckoutQuantity = 'true';
      form.appendChild(canonical);
    }
    // Do not let legacy duplicate hidden controls win during serialization.
    // Keep user-editable quantity inputs alone if they are the canonical input.
    [...form.elements].forEach(el => {
      if (el === canonical || el.name !== 'quantity' || el.disabled) return;
      if (!form.contains(el) && el.getAttribute('form') === form.id) {
        el.removeAttribute('form');
      } else if (el.type === 'hidden') {
        el.disabled = true;
      }
    });
    return canonical;
  }
  // Isolated Shopify native accelerated checkout: its form owns exactly one
  // product variant and ONE quantity input, regardless of Shrine's quantity
  // break radio buttons and external form="product-form-..." association.
  function syncNativePaymentForm(section, variant, quantity, mainForm) {
    const paymentForm = document.getElementById(`retrowave-payment-form-${section}`);
    if (!paymentForm) return;
    const paymentQuantity = paymentForm.querySelector('input.retrowave-native-quantity[name="quantity"]');
    const paymentVariant = paymentForm.querySelector('input.retrowave-native-variant-id[name="id"]');
    if (paymentQuantity && String(paymentQuantity.value) !== String(quantity)) {
      paymentQuantity.value = String(quantity);
      paymentQuantity.setAttribute('value', String(quantity));
      paymentQuantity.dispatchEvent(new Event('input', { bubbles: true }));
      paymentQuantity.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const currentId = variant?.id || mainForm?.querySelector('input[name="id"]')?.value;
    if (paymentVariant && currentId && String(paymentVariant.value) !== String(currentId)) {
      paymentVariant.value = String(currentId);
      paymentVariant.setAttribute('value', String(currentId));
      paymentVariant.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  function syncCheckoutInputs(section, variant) {
    const form = formFor(section);
    if (!form) return;
    const selected = activeBreak(section);
    const pickerInput = document.getElementById(`Quantity-Form-${section}`)?.querySelector('.quantity__input');
    // Read the customer's actual bundle selection BEFORE normalizing fields.
    const existing = form.querySelector('input[name="quantity"]:not([data-retrowave-checkout-quantity])');
    const quantity = Math.max(1, Number.parseInt(
      selected?.quantity ?? pickerInput?.value ?? existing?.value ?? '1', 10
    ) || 1);
    const quantityInput = checkoutQuantityControl(section, form);
    if (String(quantityInput.value) !== String(quantity)) {
      quantityInput.value = String(quantity);
      quantityInput.dispatchEvent(new Event('input', {bubbles: true}));
      quantityInput.dispatchEvent(new Event('change', {bubbles: true}));
    }
    if (variant) {
      const id = form.querySelector('input[name="id"]');
      if (id && String(id.value) !== String(variant.id)) {
        id.value = String(variant.id);
        id.dispatchEvent(new Event('change', {bubbles: true}));
      }
    }
    syncNativePaymentForm(section, variant, quantity, form);
    return quantity;
  }
  function syncForm(section, variant) {
    const form = formFor(section);
    if (!form || !variant) return;
    syncCheckoutInputs(section, variant);
    [form, document.getElementById(`product-form-installment-${section}`)].filter(Boolean).forEach(f => {
      const input = f.querySelector('input[name="id"]');
      if (input && String(input.value) !== String(variant.id)) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', {bubbles: true}));
      }
    });
    // Both ATC and the original Shrine skip-cart button must reflect stock.
    const secondary = document.getElementById(`SectionAtcBtn-${section}`);
    if (secondary) secondary.disabled = !variant.available;
    const btn = document.getElementById(`ProductSubmitButton-${section}`);
    if (btn) {
      btn.disabled = !variant.available;
      btn.setAttribute('aria-disabled', String(!variant.available));
      btn.dataset.unavailable = String(!variant.available);
      btn.querySelector('.main-atc__label__text') &&
        (btn.querySelector('.main-atc__label__text').textContent = variant.available ? (window.variantStrings?.addToCart || 'Add to cart') : (window.variantStrings?.soldOut || 'Sold out'));
    }
  }
  const inflight = new Map();
  function updatePriceFromServer(picker, variant) {
    const section = sectionFor(picker);
    if (!section || !variant) return;
    const token = Symbol('variant-refresh');
    inflight.set(section, token);
    const url = new URL(picker.dataset.url || location.pathname, location.origin);
    url.searchParams.set('variant', String(variant.id));
    url.searchParams.set('section_id', picker.dataset.originalSection || section);
    fetch(url.toString(), {credentials: 'same-origin'}).then(r => {
      if (!r.ok) throw new Error(`Product section HTTP ${r.status}`);
      return r.text();
    }).then(html => {
      if (inflight.get(section) !== token) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const orig = picker.dataset.originalSection || section;
      const current = document.getElementById(`price-${section}`);
      const fresh = parsed.getElementById(`price-${orig}`);
      if (current && fresh) current.innerHTML = fresh.innerHTML;
      const block = qtyFor(section);
      const newBlock = parsed.getElementById(`quantity-breaks-${orig}`);
      if (block && newBlock) {
        const oldNodes = block.querySelectorAll('.variant-price-update');
        const newNodes = newBlock.querySelectorAll('.variant-price-update');
        if (oldNodes.length === newNodes.length) oldNodes.forEach((el, i) => {
          el.innerHTML = newNodes[i].innerHTML;
          el.classList.toggle('hidden', newNodes[i].classList.contains('hidden'));
        });
      }
      syncForm(section, variant);
      updatePrice(section, variant);
    }).catch(e => {
      console.warn('[Retrowave] Variant section refresh failed; using local price fallback:', e);
      if (inflight.get(section) === token) { syncForm(section, variant); updatePrice(section, variant); }
    });
  }
  function refreshPicker(picker) {
    const section = sectionFor(picker);
    const variant = chosenVariant(picker);
    if (!section || !variant) return;
    syncForm(section, variant);
    updatePrice(section, variant);
    updatePriceFromServer(picker, variant);
  }

  // Initialize the already-selected Quantity Break on the very first render.
  // Shopify's Liquid price block renders one unit, even when Buy 2 / Buy 3
  // is preselected. A change event never fires for a prechecked radio.
  function initializeSelectedBundlePrices(container = document) {
    container.querySelectorAll('product-form.main-product-form').forEach(owner => {
      const section = owner.dataset.section;
      if (section) syncCheckoutInputs(section, chosenVariant(productPicker(section)));
    });
    container.querySelectorAll('quantity-breaks[data-update-pdp-prices="true"]').forEach(qb => {
      const section = qb.dataset.section;
      if (!section || !qb.querySelector('input[name="quantity"]:checked')) return;
      const picker = productPicker(section);
      const variant = selectedVariantForSection(section);
      if (!variant) return;
      // For a single-variant product, don't touch Shrine's stock/button state.
      if (picker) syncForm(section, variant);
      else syncCheckoutInputs(section, variant);
      updatePrice(section, variant);
    });
  }
  function scheduleInitialPriceSync(container = document) {
    // Run after theme initializers and preserve a customer's latest selection.
    requestAnimationFrame(() => initializeSelectedBundlePrices(container));
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleInitialPriceSync(), {once: true});
  } else {
    scheduleInitialPriceSync();
  }

  document.addEventListener('shopify:section:load', event => scheduleInitialPriceSync(event.target));

  // Shrine's QuickAddModal injects an entire product detail section into the
  // home/collection page. Native DOMContentLoaded has already fired by then.
  // Initialize its default Buy 2/Buy 3 price AFTER modal HTML was inserted,
  // with section IDs rewritten by quick-add.js.
  document.addEventListener('retrowave:quick-add:ready', event => {
    const content = event.target.querySelector('[id^="QuickAddInfo-"]');
    if (content) scheduleInitialPriceSync(content);
  });

  // Our listener executes even when the theme's image/section handler throws.
  document.addEventListener('change', e => {
    const picker = e.target.closest('variant-selects');
    if (picker && picker.id.startsWith('variant-selects-')) {
      queueMicrotask(() => refreshPicker(picker));
      return;
    }
    const qty = e.target.closest('quantity-breaks');
    if (qty && e.target.matches('input[name="quantity"]')) {
      const section = qty.dataset.section;
      queueMicrotask(() => {
        const v = selectedVariantForSection(section);
        syncCheckoutInputs(section, v);
        if (v) updatePrice(section, v);
      });
    }
    if (e.target.matches('.quantity__input')) {
      const sectionId = e.target.closest('[id^="Quantity-Form-"]')?.id?.replace('Quantity-Form-', '');
      if (sectionId) queueMicrotask(() => syncCheckoutInputs(sectionId, chosenVariant(productPicker(sectionId))));
    }
    // Some themes use a separate sticky selector; native sticky listener should sync the main picker.
    const sticky = e.target.closest('secondary-variant-select, secondary-variant-select-separate');
    if (sticky) queueMicrotask(() => refreshPicker(productPicker(sticky.dataset.section)));
  }, true);

  function showError(form, msg) {
    const wrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
    if (wrapper) {
      wrapper.hidden = false;
      const text = wrapper.querySelector('.product-form__error-message');
      if (text) text.textContent = msg;
    }
    console.warn('[Retrowave] Add to cart:', msg);
  }
  const addUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.replace(/\/?$/, '/') + 'cart/add.js';
  };
  function lineItems(form, section, variant) {
    const qb = qtyFor(section);
    const choice = activeBreak(section);
    const qty = choice?.quantity || parseInt(document.getElementById(`Quantity-Form-${section}`)?.querySelector('.quantity__input')?.value || form.querySelector('[name="quantity"]')?.value || '1', 10) || 1;
    const ids = qb?.hasVariants && Array.isArray(qb.formVariants) && qb.formVariants.length ? qb.formVariants : null;
    const raw = ids || Array(qty).fill(variant.id);
    const counts = new Map();
    raw.forEach(id => { if (id != null && String(id)) counts.set(String(id), (counts.get(String(id)) || 0) + 1); });
    const properties = {};
    new FormData(form).forEach((v, key) => {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match && typeof v === 'string') properties[match[1]] = v;
    });
    return [...counts].map(([id, quantity]) => ({id: Number(id), quantity, ...(Object.keys(properties).length ? {properties} : {})}));
  }
  // Shrine's original CartDrawer.renderContents() clears "is-empty" on the
  // inner node but not consistently on <cart-drawer> itself. base.css hides
  // the header, body and footer while the outer element is still is-empty.
  // The result is the white, blank drawer shown after the first successful add.
  function clearEmptyDrawerState(drawer) {
    drawer.classList.remove('is-empty');
    drawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
  }
  function hasDrawerContent(drawer) {
    return Boolean(drawer.querySelector('#CartDrawer .drawer__header') &&
      drawer.querySelector('#CartDrawer .cart-drawer__body') &&
      drawer.querySelector('#CartDrawer .drawer__footer'));
  }
  function validCartSection(html) {
    if (!html || typeof html !== 'string') return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const node = parsed.querySelector('#CartDrawer');
    return node?.querySelector('.drawer__header') &&
      node?.querySelector('.cart-drawer__body') &&
      node?.querySelector('.drawer__footer') ? node : null;
  }
  async function updateDrawer(response) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) { location.assign((window.Shopify?.routes?.root || '/') + 'cart'); return; }
    try {
      if (!validCartSection(response.sections?.['cart-drawer'])) throw new Error('Invalid initial cart-drawer HTML');
      if (typeof drawer.renderContents !== 'function') throw new Error('Native cart drawer is unavailable');
      // MUST happen before renderContents(): CSS hides every main cart block
      // when the root cart-drawer element still has the is-empty class.
      clearEmptyDrawerState(drawer);
      drawer.renderContents(response);
      clearEmptyDrawerState(drawer);
      if (!hasDrawerContent(drawer)) throw new Error('Cart drawer rendered without body/header/footer');
      return; // Native Shrine method opens the drawer once rendering completes.
    } catch (error) {
      console.warn('[Retrowave] Native cart drawer update failed:', error);
    }
    try {
      const cartUrl = new URL(window.Shopify?.routes?.cart_url ||
        (window.Shopify?.routes?.root || '/') + 'cart', location.origin);
      cartUrl.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      // Retain the preview-theme context when Shopify preview passes it in the URL.
      for (const key of ['preview_theme_id', '_fd', 'pb']) {
        const val = new URLSearchParams(location.search).get(key);
        if (val) cartUrl.searchParams.set(key, val);
      }
      const res = await fetch(cartUrl.toString(), {credentials: 'same-origin', cache: 'no-store'});
      if (!res.ok) throw new Error(`Cart section fetch HTTP ${res.status}`);
      const sections = await res.json();
      const incoming = validCartSection(sections['cart-drawer']);
      const existing = drawer.querySelector('#CartDrawer');
      if (!incoming || !existing) throw new Error('Cart drawer HTML missing required blocks');
      // Replace only the content of the original Shopify cart drawer.
      existing.innerHTML = incoming.innerHTML;
      clearEmptyDrawerState(drawer);
      if (!hasDrawerContent(drawer)) throw new Error('Cart drawer content is still incomplete');
      const iconHTML = sections['cart-icon-bubble'];
      if (iconHTML) {
        const icon = new DOMParser().parseFromString(iconHTML,'text/html').querySelector('#cart-icon-bubble');
        const currentIcon = document.querySelector('#cart-icon-bubble');
        if (icon && currentIcon) currentIcon.innerHTML = icon.innerHTML;
      }
      if (typeof drawer.open === 'function') drawer.open();
      else { drawer.classList.add('active'); document.body.classList.add('overflow-hidden'); }
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close?.(), {once:true});
    } catch (error) {
      console.error('[Retrowave] Cannot display the cart drawer; directing to cart page:', error);
      // Never leave customers trapped behind an empty overlay.
      drawer.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      location.assign((window.Shopify?.routes?.root || '/') + 'cart');
    }
  }
  const busy = new WeakSet();
  // Avoid interfering with complex subscriptions, gifts and third-party bundle engines.
  // Their cart line items must be preserved by the native handler.
  function canSafelyIntercept(form) {
    const section = form.closest('product-form.main-product-form')?.dataset.section;
    if (!section) return false;
    if (document.getElementById(`bundle-deals-${section}`) || document.getElementById(`quantity-gifts-${section}`)) return false;
    if (form.querySelector('[name="selling_plan"]') || form.closest('product-form')?.querySelector('[name="selling_plan"]')) return false;
    if ([...document.querySelectorAll(`.product-info-upsell-${escapeId(section)}`)].some(e => e.dataset.selected === 'true')) return false;
    return true;
  }
  async function submitMainForm(form) {
    if (busy.has(form)) return;
    const owner = form.closest('product-form.main-product-form');
    const section = owner?.dataset.section;
    if (!section) return;
    const picker = productPicker(section);
    const variant = chosenVariant(picker) || null;
    const id = Number(variant?.id || form.querySelector('input[name="id"]')?.value);
    if (!id || (variant && !variant.available)) {showError(form, 'Please select an available option.'); return;}
    busy.add(form);
    const button = form.querySelector('button[name="add"]');
    if (button) {button.disabled = true; button.classList.add('loading');}
    const errorWrap = owner.querySelector('.product-form__error-message-wrapper');
    if (errorWrap) errorWrap.hidden = true;
    try {
      const payload = {items: lineItems(form, section, variant || {id}), sections: 'cart-drawer,cart-icon-bubble', sections_url: location.pathname};
      if (!payload.items.length || payload.items.some(v => !v.id || !v.quantity)) throw new Error('No valid product variant or quantity');
      const res = await fetch(addUrl(), {method: 'POST', credentials:'same-origin', headers:{'Content-Type':'application/json', 'Accept':'application/json'}, body:JSON.stringify(payload)});
      const body = await res.json();
      if (!res.ok || body.status && body.status >= 400) throw new Error(body.description || body.message || `HTTP ${res.status}`);
      await updateDrawer(body);
    } catch (error) { showError(form, error.message || 'Could not add to cart. Please try again.'); }
    finally {
      busy.delete(form);
      if (button) {button.classList.remove('loading'); if (!variant || variant.available) button.disabled = false;}
    }
  }
  // Secondary Shrine BUY IT NOW must checkout ONLY the selected line items.
  // Unlike /cart/add.js followed by /checkout, a Shopify cart permalink
  // creates a checkout for these selected variants instead of the old cart.
  // See Shopify's official cart permalink documentation.
  function directCheckoutURL(items) {
    const base = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const clean = items.filter(item => /^\d+$/.test(String(item.id)) &&
      Number.isInteger(Number(item.quantity)) && Number(item.quantity) > 0);
    if (clean.length !== items.length || !clean.length) throw new Error('Invalid selection.');
    const segments = clean.map(item => `${item.id}:${item.quantity}`).join(',');
    const url = new URL(base + 'cart/' + segments, location.origin);
    // Shopify allows properties on the first line of cart permalinks.
    // Don't silently discard customer-entered properties, when present.
    const properties = clean[0].properties;
    if (properties && Object.keys(properties).length) {
      const json = JSON.stringify(properties);
      const bytes = new TextEncoder().encode(json);
      let raw = '';
      for (const byte of bytes) raw += String.fromCharCode(byte);
      url.searchParams.set('properties', btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''));
    }
    return url.toString();
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('button[data-skip-cart-button="true"]');
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.disabled) return;
    const owner = button.closest('product-form.main-product-form');
    const form = owner?.querySelector('form[data-type="add-to-cart-form"]');
    const section = owner?.dataset.section;
    if (!form || !section || busy.has(form)) return;
    const picker = productPicker(section);
    const variant = chosenVariant(picker);
    const id = Number(variant?.id || form.querySelector('input[name="id"]')?.value);
    if (!id || (variant && !variant.available)) {
      showError(form, 'Please select an available option.');
      return;
    }
    if (form.querySelector('[name="selling_plan"]:not([disabled])')?.value) {
      showError(form, 'Subscriptions need the normal checkout flow.');
      return;
    }
    syncCheckoutInputs(section, variant);
    const items = lineItems(form, section, variant || {id});
    try {
      const url = directCheckoutURL(items);
      button.classList.add('loading');
      // No /cart/add.js, /cart/clear.js or mutation to the existing cart.
      window.location.assign(url);
    } catch (error) {
      button.classList.remove('loading');
      showError(form, error.message || 'Unable to continue to checkout.');
    }
  }, true);

  // Shopify's accelerated checkout is rendered inside the product form.
  // Ensure it sees the current variant ID + single quantity field BEFORE
  // the native component handles a pointer or keyboard activation.
  // We never preventDefault or replace its real payment behavior.
  function syncAcceleratedCheckout(target) {
    const owner = target?.closest?.('product-form.main-product-form');
    const form = owner?.querySelector('form[data-type="add-to-cart-form"]');
    const section = owner?.dataset.section;
    if (!form || !section) return;
    const variant = chosenVariant(productPicker(section));
    syncCheckoutInputs(section, variant);
    // Native accelerated-checkout web components sometimes read the form's
    // change notifications rather than merely inspecting its input value.
    const id = form.querySelector('input[name="id"]');
    if (id) id.dispatchEvent(new Event('change', {bubbles: true}));
  }
  function onAcceleratedInteraction(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const target = path.find(node => node?.matches?.('.shopify-payment-button, shopify-accelerated-checkout')) ||
      event.target?.closest?.('.shopify-payment-button, shopify-accelerated-checkout');
    if (target) syncAcceleratedCheckout(target);
  }
  ['pointerdown', 'click', 'keydown'].forEach(type =>
    document.addEventListener(type, onAcceleratedInteraction, true));

  // Capture before old theme submit handler; prevents duplicate add requests.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('form[data-type="add-to-cart-form"]') || !form.closest('product-form.main-product-form') || !canSafelyIntercept(form)) return;
    // Intercept ONLY the actual Add to Cart button. Shopify's dynamic checkout
    // can submit this very same form, and must not be converted to Ajax add.
    const submitter = event.submitter;
    if (!submitter || !submitter.matches('button[name="add"][id^="ProductSubmitButton-"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    submitMainForm(form);
  }, true);
  document.addEventListener('click', event => {
    const button = event.target.closest('sticky-atc .sticky-atc__add_button');
    if (!button) return;
    const sticky = button.closest('sticky-atc');
    const section = sticky?.dataset.section || sticky?.id?.replace(/^sticky-atc-/, '');
    const form = formFor(section);
    if (!form || !canSafelyIntercept(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    submitMainForm(form);
  }, true);
})();
